















































































































































import { Vue, Component, Watch, Prop } from "@/shared/vuepack";
import { Validator } from "vee-validate";
import { Location } from "vue-router";
import UploadFileForm from "@/admin/components/partners/UploadFileForm.vue";
import PartnerUserBulkActions from "@/admin/components/partners/PartnerUserBulkActions.vue";
import HowItWorksWidget from "@/employer/components/shared/HowItWorksWidget.vue";
import Pagination from "@/shared/components/ui/Pagination.vue";
import PageSizeSelectList from "@/shared/components/ui/PageSizeSelectList.vue";
import Pill from "@/shared/components/ui/Pill.vue";
import { useNotifierStore } from "@/shared/store/notifier";
import { usePartnerStore } from "@/admin/store/partner";
import { BulkUploadFile, FileUploadRequest, BulkFileUploadStatus, BulkFileUploadType } from "@/admin/types";
import { usePageSizeStore } from "@/shared/store/pageSize";
import PartnerUserBulkDeleteModal from "./PartnerUserBulkDeleteModal.vue";
import PartnerUserBulkErrorModal from "./PartnerUserBulkErrorModal.vue";
import PartnerUserFileSearch from "@/admin/components/partners/PartnerUserFileSearch.vue";
import { FetchState } from "@/shared/types";

@Component({
  components: {
    PartnerUserFileSearch,
    UploadFileForm,
    HowItWorksWidget,
    Pagination,
    PageSizeSelectList,
    Pill,
    PartnerUserBulkActions,
    PartnerUserBulkDeleteModal,
    PartnerUserBulkErrorModal
  }
})
export default class PartnerUserBulkDelete extends Vue {
  $validator: Validator;
  notifierStore = useNotifierStore();
  partnerStore = usePartnerStore();
  pageSizeStore = usePageSizeStore();
  isUploading = false;
  isFormDisabled = false;
  isLoading = true;

  fileType = BulkFileUploadType.MassDelete;

  @Prop()
  pageNumber: number;

  @Prop()
  routeToBulkDeleteView: (page: number) => Location;

  mounted (): void {
    this.pageSizeStore.setPageSize(10);
    this.partnerStore.getAllBulkFiles(this.pageNumber, this.pageSizeStore.pageSize, BulkFileUploadType.MassDelete)
      .then(() => {
        this.isLoading = false;
      });

    this.$on("refreshFileUpload", () => {
      // on switching page size, reset to page 1
      this.pageNumber = 1;
      this.$router.push(this.routeToBulkDeleteView(1));
      this.getUploadedFiles();
    });
  }

  getUploadedFiles () {
    // @ts-ignore
    this.$refs.searchFormRef.onSearch();
  }

  uploadMassDelete (file: FileUploadRequest) {
    this.isUploading = true;
    this.isFormDisabled = true;
    return this.partnerStore.uploadBulkDeleteFile(file)
      .finally(() => {
        this.isUploading = false;
        this.isFormDisabled = false;

        this.getUploadedFiles();
      });
  }

  switchPage (toPage: number): void {
    this.$router.push(this.routeToBulkDeleteView(toPage));
  }

  @Watch("pageNumber")
  onRouteValuePageNumberChanged (): void {
    this.getUploadedFiles();
  }

  @Watch("partnerStore.bulkFilesFetchState")
  setLoadingState () {
    this.isLoading = this.partnerStore.bulkFilesFetchState === FetchState.Loading;
  }

  switchPageSize (selectedPageSize: number): void {
    // on switching page size, reset to page 1
    this.pageSizeStore.setPageSize(selectedPageSize);
    this.pageNumber = 1;
    this.getUploadedFiles();
    this.$router.push(this.routeToBulkDeleteView(1));
  }

  getPageCount (): number {
    if (this.partnerStore.getBulkFiles()) {
      return Math.ceil(this.partnerStore.getBulkFiles().totalRecords / this.pageSizeStore.pageSize);
    }

    return 0;
  }

  get fileFormattingGuidelinesModal (): string {
    return "partner-user-bulk-delete-modal";
  }

  get fileTemplate () {
    return "/partner_user_bulk_delete_template.csv";
  }

  getPillClass (file: BulkUploadFile): string {
    if (this.isFileError(file)) {
      return "error-pill";
    }
    else if (this.isFilePending(file)) {
      return "neutral-pill";
    }
    else {
      return "success-pill";
    }
  }

  getStatusText (file: BulkUploadFile): string {
    switch (file.status) {
      case BulkFileUploadStatus.MessagePublished: {
        return "Received";
      }
      case BulkFileUploadStatus.ErrorPublishingMessage:
      case BulkFileUploadStatus.ErrorUploadingFile: {
        return "Upload Error";
      }
      default: {
        return file.status;
      }
    }
  }

  handleError (err: any): void {
    this.notifierStore.setDanger(err || "Error processing your request. Please try again later");
  }

  getDateFormatted (dateUploaded: string): string {
    const dateObject = new Date(dateUploaded);
    const formattedDate = dateObject.toLocaleDateString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit"
    });
    return formattedDate;
  }

  isFileError (file: BulkUploadFile): boolean {
    return file &&
      file.status &&
      (file.status === BulkFileUploadStatus.Error ||
      file.status === BulkFileUploadStatus.ErrorUploadingFile ||
      file.status === BulkFileUploadStatus.ErrorPublishingMessage ||
      file.status === BulkFileUploadStatus.Failed);
  }

  isFilePending (file: BulkUploadFile): boolean {
    return file && file.status &&
      (
        file.status === BulkFileUploadStatus.None ||
        file.status === BulkFileUploadStatus.MessagePublished ||
        file.status === BulkFileUploadStatus.Processing ||
        file.status === BulkFileUploadStatus.Validating ||
        file.status === BulkFileUploadStatus.Uploading ||
        file.status === BulkFileUploadStatus.Uploaded ||
        file.status === BulkFileUploadStatus.Pending
      );
  }

  resultFileDownloadButtonVariant (file: BulkUploadFile) {
    if (this.isFileError(file)) {
      return "danger";
    }
    else if (this.isFilePending(file)) {
      return "light";
    }
    else {
      return "outline-success";
    }
  }

  resultFileDownloadButtonIcon (file: BulkUploadFile) {
    if (this.isFileError(file)) {
      return "fa-message-exclamation";
    }
    else if (this.isFilePending(file)) {
      return "fa-hourglass-half";
    }
    else {
      return "fa-download";
    }
  }

  onResultFileDownloadClick (file: BulkUploadFile): void {
    if (this.isFileError(file)) {
      this.errorFile = file;
      this.$root.$emit("bv::toggle::modal", "bulk-error-modal", "#modal-toggle");
    } else if (!this.isFilePending(file)) {
        this.partnerStore.downloadBulkFileResult(file.partnerUserFileId);
    }
  }

  errorFile: BulkUploadFile = null;
}
